<template>
  <div class="datasheet-form">
    <div v-if="element" class="datasheet-organisms-single-line-text-item p-2 no-validation-rules">
      <div class="datasheet-organisms-order-number" style="font-size: 12px">
        {{ element.orderNumber }}
      </div>
      <HeaderRecordTitleHint
        v-if="element.recordType === 'description'"
        :record="element"
        :showUntitledLabel="showUntitledLabel"
        :untitledRecord="untitledRecord"/>
      <RecordTitleHint
        v-else
        :record="element"
        :showUntitledLabel="showUntitledLabel"
        :untitledRecord="untitledRecord"/>
      <div v-if="element.recordType === 'textarea'">
        <b-form-textarea :id="element['@id']" v-model="element.defaultValue" class="my-1" rows="2"
                         v-validate=element.validatorRules :data-vv-name="element['@id'] || 'name'"
                         :data-vv-as="element.recordType"
                         :required="element.requiredField"/>
        <label v-if="errors.first(element['@id'])" class="text-danger">
          {{ errors.first(element['@id']) }}
        </label>
      </div>

      <div v-else-if="element.recordType === 'text'">
        <b-form-input :id="element['@id']" v-model="element.defaultValue" class="my-1" v-validate=element.validatorRules
                      :data-vv-name="element['@id'] || 'name'" :data-vv-as="element.recordType"
                      :required="element.requiredField"/>
        <label v-if="errors.first(element['@id'])" class="text-danger">
          {{ errors.first(element['@id']) }}
        </label>
      </div>

      <div v-else-if="element.recordType === 'number'">
        <div class="input-group w-75">
          <input type="number" :no-wheel="true" v-model="element.defaultValue" class="form-control" v-validate=element.validatorRules
                 :data-vv-name="element['@id'] || 'name'" :data-vv-as="element.recordType"
                 :required="element.requiredField"/>
          <div class="input-group-append">
            <button class="btn px-4 btn-outline-secondary" type="button" @click="increment('decrease')">
              <i class="fa fa-minus"></i>
            </button>
            <button class="btn px-4 btn-secondary" type="button" @click="increment('increase')">
              <i class="fa fa-plus"></i>
            </button>
          </div>
        </div>
        <label v-if="errors.first(element['@id'])" class="text-danger">
          {{ errors.first(element["@id"]) }}
        </label>
      </div>

      <div v-else-if="element.recordType === 'dropdown'">
        <b-form-select :id="element['@id']" v-model="previewSelection" :options="element.optionsSet.optionsValues"
                       text-field="value" value-field="orderNumber" v-validate=element.validatorRules
                       :data-vv-name="element['@id'] || 'name'" data-vv-as="dropdown - single selection"
                       :required="element.requiredField">
          <template #first>
            <b-form-select-option :value="null" disabled>
              - Select an option -
            </b-form-select-option>
          </template>
        </b-form-select>
        <label v-if="errors.first(element['@id'])" class="text-danger">
          {{ errors.first(element["@id"]) }}
        </label>
      </div>

      <div v-else-if="element.recordType === 'multiple_choice'">
        <v-select class="p-0" label="- Select one or more -" v-model="previewMultiSelection"
                  :items="element.optionsSet.optionsValues" item-text="value" item-value="orderNumber" multiple single-line
                  outline
                  :required="element.requiredField"/>
      </div>

      <div v-else-if="element.recordType === 'datetime'">
        <div class="datetime-picker-record">
          <div class="date-type-picker-wrapper">
            <date-picker placeholder="YYYY/MM/DD h:mm:ss A" :config="datetimePickerOptions" :wrap="true"
                         v-model="element.defaultValue" v-validate=element.validatorRules
                         :data-vv-name="element['@id'] || 'name'"
                         :data-vv-as="element.recordType" :required="element.requiredField"/>
            <label v-if="errors.first(element['@id'])" class="text-danger">
              {{ errors.first(element["@id"]) }}
            </label>
          </div>
        </div>
      </div>

      <div v-else-if="element.recordType === 'radio'">
        <div class="form-group mb-0">
          <b-form-radio-group
            :id="element['@id'] || element.marker"
            v-model="previewSelection"
            :options="element.optionsSet.optionsValues"
            v-validate=element.validatorRules
            :data-vv-name="element['@id'] || element.marker"
            :data-vv-as="element.recordType"
            :required="element.requiredField"
            text-field="value"
            value-field="orderNumber"
            stacked
          ></b-form-radio-group>
          <label :for="element['@id'] || element.marker" class="text-danger">
            {{ errors.first(element['@id'] || element.marker) }}
          </label>
        </div>
      </div>

      <div v-else-if="element.recordType === 'image'">
        <RecordImages :record="element" :isPreview="true"></RecordImages>
      </div>

      <div class="btn-group-vertical position-absolute" style="top: 0px; right: -38px"
           v-if="activeRecordMarker === element.marker">
        <button class="btn btn-danger text-white bg-danger" @click="modalIsOpen = true" alt="delete">
          <i v-b-tooltip.hover.left title="Delete Record" class="fa fa-lg fa-trash datasheet-record-option"></i>
        </button>
      </div>

    </div>
  </div>
</template>

<script lang="js">
import DatePicker from 'vue-bootstrap-datetimepicker'
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css'

// Using font-awesome 5 icons
import '@fortawesome/fontawesome-free/css/fontawesome.css'
import '@fortawesome/fontawesome-free/css/regular.css'
import '@fortawesome/fontawesome-free/css/solid.css'

import RecordImages from "../datasheet/RecordImages.vue"
import PrivateTooltip from '../common/PrivateTooltip.vue'
import RecordTitleHint from './RecordTitleHint.vue'
import HeaderRecordTitleHint from './HeaderRecordTitleHint.vue'

// Using font-awesome 5 icons
$.extend(true, $.fn.datetimepicker.defaults, {
  icons: {
    time: 'far fa-clock',
    date: 'far fa-calendar',
    up: 'fas fa-arrow-up',
    down: 'fas fa-arrow-down',
    previous: 'fas fa-chevron-left',
    next: 'fas fa-chevron-right',
    today: 'fas fa-calendar-check',
    clear: 'far fa-trash-alt',
    close: 'far fa-times-circle'
  }
})

const DATETIME_PICKER_OPTIONS = {
  allowInputToggle: true,
  format: "YYYY/MM/DD h:mm:ss A",
  showClear: true,
  showClose: true,
  useStrict: true,
}

const UNTITLED_RECORD = "Untitled Question"

export default {
  name: 'datasheetRecord',
  inject: ['parentValidator'],

  components: {
    DatePicker,
    RecordImages,
    PrivateTooltip,
    RecordTitleHint,
    HeaderRecordTitleHint,
  },

  props: {
    element: {
      type: Object,
      default: () => {
      }
    },

    activeRecordMarker: {
      type: String,
      default: () => {
      }
    },
    duplicateDatasheetRecord: {
      type: Function,
      default: () => {
      }
    },
    removeDatasheetRecordModalIsOpen: {
      type: Boolean,
      default: false
    },

    showUntitledLabel: {
      type: Boolean,
      default: true
    },

    uploadLoadingStatus: {
      type: Boolean,
      default: false
    },

    page: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      untitledRecord: UNTITLED_RECORD,
      removeRecordModalIsOpen: this.removeDatasheetRecordModalIsOpen,
      datetimePickerOptions: DATETIME_PICKER_OPTIONS,
      uploadLoadingStatusValue: this.uploadLoadingStatus,
    }
  },

  watch: {
    element(newVal) {
      console.log(newVal)
    }
  },

  mounted() {
    window.onmousedown = function (e) {
      var el = e.target;
      if (el.tagName.toLowerCase() == 'option' && el.parentNode.hasAttribute('multiple')) {
        e.preventDefault();

        // toggle selection
        if (el.hasAttribute('selected')) el.removeAttribute('selected');
        else el.setAttribute('selected', '');

        // hack to correct buggy behavior
        var select = el.parentNode.cloneNode(true);
        el.parentNode.parentNode.replaceChild(select, el.parentNode);
      }
    }
  },
  created() {
    this.$validator = this.parentValidator
  },

  methods: {
    increment(direction) {
      console.log('increment')
      const oldString = this.element.defaultValue;
      let oldNum = 0
      const isUndefined = (oldString === undefined)
      const isNanString = (oldString === 'NaN')
      const isNotANumber = isNaN(oldString)

      if (!isUndefined && !isNanString && !isNotANumber) {
        oldNum = Number(oldString)
      }
      let newValue = 0;
      if (direction === 'increase') {
        newValue = Number(oldNum) + 1;
      } else if (direction === 'decrease') {
        newValue = Number(oldNum) - 1;
      }
      // check to see if value is in bounds
      const maxObj = this.element.validators.find((validator) => {
        return validator.validatorType === "max"
      })
      const minObj = this.element.validators.find((validator) => {
        return validator.validatorType === "min"
      })
      if (maxObj && maxObj.value && Number(maxObj.value) < newValue) {
        newValue = Number(maxObj.value);
      }
      if (minObj && minObj.value && Number(minObj.value) > newValue) {
        newValue = Number(minObj.value);
      }
      this.element.defaultValue = newValue.toString();
    },
  },

  computed: {
    previewSelection: {
      get() {
        return this.element.defaultOption
      },
      set() {
        // Suppress the child from modifying the parent
      }
    },
    previewMultiSelection: {
      get() {
        return this.element.defaultOptions || []
      },
      set() {
        // Suppress the child from modifying the parent
      }
    },
    modalIsOpen: {
      get: function () {
        return this.removeRecordModalIsOpen
      },
      set: function (newValue) {
        this.removeRecordModalIsOpen = newValue

        this.$emit('modalIsOpen', this.modalIsOpen)
      }
    },

    uploadLoadingStatusVal: {
      get: function () {
        return this.uploadLoadingStatusValue
      },
      set: function (newValue) {
        this.uploadLoadingStatusValue = newValue

        this.$emit('uploadLoadingStatus', this.uploadLoadingStatusVal)
      }
    }
  }
}
</script>
<style lang="scss">
.datasheet-element-description {
  font-size: 13px;
}

::selection {
  background: #87983f !important;
  color: white;
}

select > option {
  padding: 10px;
  border-radius: 5px;

  &:not(:first-of-type) {
    margin-top: 6px !important;
  }

  &:checked,
  &:active {
    background: #87983f !important;
    color: white;
  }
}

#custom-vuetify-style {
  .datasheet-organisms-order-number {
    top: 14px;
    position: absolute;
    left: -35px;
    background: #eee;
    border: 1px solid #dce0e6;
    width: auto;
    min-width: 30px;
    padding: 4px 0;
    text-align: center;
  }

  .datasheet-record-options {
    top: -10px;
    position: absolute;
    right: -41px;
    background: #95daf6;
    border: 1px solid #dce0e6;
    width: auto;
    min-width: 30px;
    padding: 4px 0;
    text-align: center;

    .datasheet-record-option:hover {
      color: #c0c99c !important;
    }

    .v-icon {
      margin-bottom: 10px;
    }

    .fa-clone {
      color: #ffffff;
      font-size: 16px;
    }
  }

  .datasheet-record-option-value-preview-side ::v-deep {
    .v-label {
      color: #0000008a !important;
    }

    .v-input__control {
      width: 100%;

      .v-input__slot {
        margin: 0;

        .v-input--radio-group__input {
          > .v-label {
            border-bottom: 1px solid #dce0e6;
            font-size: 15px;
          }

          .option-value-preview-side {
            border-bottom: 1px solid #dce0e6;
            margin: 0 0 0 35px;
            padding: 7px 16px 0 0;
            position: relative;

            &:last-child {
              border: none;
            }
          }
        }

        .v-input--selection-controls__input {
          position: absolute;
          top: 7px;
          right: 4px;

          .v-icon,
          .v-icon.accent--text {
            font-size: 20px;
            color: #87983f !important;
            caret-color: #87983f !important;
          }
        }

        .v-label,
        .single-choice-custom-label {
          width: 100%;
        }

        .single-choice-custom-label {
          font-weight: normal;
          font-size: 14px;

          .v-icon {
            float: right;
          }
        }
      }

      .v-messages {
        font-weight: normal;
      }
    }
  }

  .datasheet-image-record-preview-side {
    p {
      color: #323c47b3;
      font-size: 15px;

      &.required-asterisk:after {
        content: " *";
        color: #ff5252;
      }
    }

    .theme--light.v-btn.v-btn--disabled {
      background-color: transparent !important;
      text-transform: initial;
    }

    .image-record-wrapper .v-btn ::v-deep {
      padding: 0;

      .v-btn__content {
        height: 36px;
        line-height: 36px;

        .file-uploads {
          width: 100%;
        }
      }
    }
  }

  .organism-record-select-organism-dropdown ::v-deep {
    padding-top: 12px;

    .v-input__slot {
      background: #ffffff00 !important;
      border-color: #00000080;
      margin-top: 1.5px;
      border-width: thin;
      height: 34px;
      min-height: 24px;
      padding: 0 6px;
      border-bottom: 1px solid #00000080 !important;

      &:hover {
        border-width: thin !important;
      }

      .v-label {
        color: #000000cc;
        padding-left: 0 !important;
        top: 7px;
      }

      .v-input__append-inner {
        margin-top: 5px;
      }
    }

    .v-input__prepend-outer {
      color: #323c47b3;
      font-size: 15px;
      min-width: 30%;
      margin-top: 12px;
    }
  }

  .datetime-picker-record {
    display: flex;
    padding-top: 10px;
    margin-bottom: 10px;

    label {
      min-width: 30%;
      margin-right: 9px;
      line-height: 36.5px;
      font-size: 15px;
      color: #323c47b3;
    }

    .date-type-picker-wrapper {
      position: relative;
      width: 100%;

      input {
        border: 1px solid #0000008a;
        font-size: 15px;

        &:hover,
        &:active,
        &:focus {
          box-shadow: none;
          -moz-box-shadow: none;
          -webkit-box-shadow: none;
        }

        &:disabled {
          background-color: transparent;
        }

        &.is-invalid {
          border-color: #dc3545;
        }
      }

      ::v-deep .bootstrap-datetimepicker-widget {
        position: absolute;
        left: 0;

        &.bottom {
          top: 34.5px !important;
        }
      }
    }
  }

  .datetime-picker-record .date-type-picker-wrapper,
  .datasheet-image-record-preview-side .image-record-wrapper {
    .v-text-field__details {
      margin-top: 8px;
      padding: 0 12px;

      .invalid-feedback {
        display: block;
        width: 100%;
        margin-top: 0;
        font-size: 100%;
        color: #dc3545;
      }
    }
  }

  .datasheet-view-records {
    top: 10px;
  }

  .datasheet-view-image-record {
    top: 38px;
  }
}

/** WIP - Style */
select[multiple="multiple"] > option {
  padding: 10px;
  margin: 12px;
}

.v-input--selection-controls {
  margin: 0px;
  padding: 0px;
}

.v-input__slot {
  margin-bottom: 0px !important;
}
</style>
